import { useState } from 'react';
import { ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import styles from './FollowupQuestionForm.module.css';

/** 标准追问选项，描述可换行，不截断模型传来的语义。 */
interface Question {
  /** 原始题目作为答案映射键，不能用展示文案替换。 */
  question: string;
  header?: string;
  /** 可选的友好说明，仅影响显示，不影响接口回传值。 */
  display?: { question: string; header?: string };
  multiSelect?: boolean;
  options: { label: string; description?: string }[];
}
/** 只处理草稿与展示，答案序列化由调用方指定，提交仍走已有接口。 */
interface Props {
  /** 当前请求的题目，切换请求时由调用方更新组件 key。 */
  questions: Question[];
  language?: string;
  /** 各端沿用原有多选分隔符，避免改变已有工具协议。 */
  multiSeparator?: string;
  /** 恢复请求中已有的答案，未知字段忽略；不同请求由外层 key 重置。 */
  initialAnswers?: unknown;
  /** 提交所有题目；异步失败应抛出异常，以便保留草稿并提示重试。 */
  onSubmit: (answers: Record<string, string>) => void | Promise<void>;
  /** 显式拒绝请求；收起抽屉不调用此回调。 */
  onDeny: () => void | Promise<void>;
}

/** 固定题干、底部输入与提交；仅选项列表滚动。外层按请求 key 隔离草稿。 */
export default function FollowupQuestionForm({ questions, language = 'zh', multiSeparator = '|||', initialAnswers, onSubmit, onDeny }: Props) {
  const [initial] = useState(() => {
    const answers = initialAnswers && typeof initialAnswers === 'object' ? initialAnswers as Record<string, unknown> : {};
    const selected: Record<number, string[]> = {};
    const other: Record<number, string> = {};
    questions.forEach((q, i) => {
      const raw = answers[q.question];
      if (typeof raw !== 'string' || !raw.trim()) return;
      const values = q.multiSelect ? raw.split(multiSeparator).map(value => value.trim()) : [raw];
      if (values.every(value => q.options.some(option => option.label === value))) selected[i] = values;
      else other[i] = raw;
    });
    return { selected, other };
  });
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<Record<number, string[]>>(initial.selected);
  const [other, setOther] = useState<Record<number, string>>(initial.other);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const zh = language.startsWith('zh');
  const currentIndex = Math.min(index, questions.length - 1);
  const question = questions[currentIndex];
  if (!question) return null;
  /** 检查指定题是否有非空自由答案或至少一个选项。 */
  const answered = (i: number) => Boolean(other[i]?.trim() || selected[i]?.length);
  const complete = questions.every((_, i) => answered(i));
  const last = currentIndex === questions.length - 1;

  /** 防止重复提交；失败保留本地草稿供用户重试。 */
  const submit = async (deny = false) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      if (deny) await onDeny();
      else await onSubmit(Object.fromEntries(questions.map((q, i) => [q.question, other[i]?.trim() || (selected[i] ?? []).join(q.multiSelect ? multiSeparator : '')])));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally { setBusy(false); }
  };
  /** 非末题先翻页；末题仍有空题时定位到首个未答项，绝不静默提交空答案。 */
  const advance = () => {
    if (!last) { setIndex(currentIndex + 1); return; }
    if (!complete) { setIndex(questions.findIndex((_, i) => !answered(i))); return; }
    void submit();
  };

  return <div className={styles.form} data-followup-question-form aria-busy={busy}>
    <div className={styles.heading}>
      {(questions.length > 1 || (question.display?.header ?? question.header)) && <div className={styles.metaRow}>
        {questions.length > 1 && <div className={styles.pages}>
          <button type="button" disabled={busy || currentIndex === 0} onClick={() => setIndex(currentIndex - 1)} aria-label={zh ? '上一题' : 'Previous question'}><ChevronLeftIcon aria-hidden="true" /></button>
          <span aria-live="polite">{currentIndex + 1} / {questions.length}</span>
          <button type="button" disabled={busy || last} onClick={() => setIndex(currentIndex + 1)} aria-label={zh ? '下一题' : 'Next question'}><ChevronRightIcon aria-hidden="true" /></button>
        </div>}
        {(question.display?.header ?? question.header) && <span className={styles.label}>{question.display?.header ?? question.header}</span>}
      </div>}
      <div className={styles.titleRow}>
        <h3>{question.display?.question ?? question.question}</h3>
        {question.multiSelect && <span className={styles.hint}>{zh ? '可多选' : 'Choose multiple'}</span>}
      </div>
    </div>
    <div className={styles.scroll}>
      <div className={styles.options} key={currentIndex} aria-label={zh ? '回答选项' : 'Answer options'}>
        {question.options.map((option, i) => {
          const checked = (selected[currentIndex] ?? []).includes(option.label);
          return <button type="button" key={i} aria-pressed={checked} disabled={busy}
            className={styles.option} onClick={() => {
              setOther(prev => ({ ...prev, [currentIndex]: '' }));
              setSelected(prev => ({ ...prev, [currentIndex]: question.multiSelect
                ? (checked ? (prev[currentIndex] ?? []).filter(value => value !== option.label) : [...(prev[currentIndex] ?? []), option.label])
                : [option.label] }));
            }}>
            <span className={styles.number}>{checked ? '✓' : i + 1}</span>
            <span><span className={styles.optionTitle}>{option.label}</span>{option.description && <span className={styles.description}>{option.description}</span>}</span>
          </button>;
        })}
      </div>
    </div>
    <div className={styles.footer}>
      {error && <div role="alert" className={styles.error}>{error}</div>}
      <div className={styles.inputRow}>
        <input aria-label={zh ? '其他答案' : 'Custom answer'} placeholder={zh ? '输入其他答案…' : 'Type another answer…'} value={other[currentIndex] ?? ''} disabled={busy}
          onChange={event => { setOther(prev => ({ ...prev, [currentIndex]: event.target.value })); setSelected(prev => ({ ...prev, [currentIndex]: [] })); }}
          onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing && answered(currentIndex)) { event.preventDefault(); advance(); } }} />
        <button type="button" className={styles.send} disabled={busy || !answered(currentIndex)}
          aria-label={last && complete ? (zh ? '提交回答' : 'Submit answers') : (zh ? '继续答题' : 'Continue')} onClick={advance}><ArrowUpIcon aria-hidden="true" /></button>
      </div>
      <button type="button" className={styles.deny} disabled={busy} onClick={() => { void submit(true); }}>{zh ? '拒绝此请求' : 'Decline request'}</button>
    </div>
  </div>;
}
